import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { AiOutlineClose } from "react-icons/ai";

import SearchForm from "./common/searchForm";
import MemberTable from "./common/memberTable";
import CloseButton from "./common/closeButton";

import { ModalBackground, NotMessage } from "../../../common/styled/project";
import { KickModalContainer } from "../../../common/styled/project";

import { ButtonStateStyle, formatPhoneNumber, memberName } from "../../../common/function/project";
import { useModal } from "../../../../../../../hooks/useModal";

const KickModal = ({ project, participantList, getParticipantList, changeModalState }) => {
  const isGrp = project?.gbn === "G";
  const user = useSelector((state) => state?.user?.data[0]);
  const { modal, openModal, closeModal } = useModal();

  const [searched, setSearched] = useState("");
  const [members, setMembers] = useState({});
  const [chkMberList, setChkMberList] = useState([]);
  const [isAllChecked, setIsAllChecked] = useState(false);
  const [isSearch, setIsSearch] = useState(false);

  // 조직별 멤버 그룹화
  const groupMembersByOrg = (list) => {
    return list.reduce((acc, cur) => {
      const { orgNo, orgNm } = cur;
      if (!acc[orgNo]) {
        acc[orgNo] = { orgNm, members: [cur] };
      } else {
        acc[orgNo].members.push(cur);
      }
      return acc;
    }, {});
  };

  // 플랫 멤버 목록 생성
  const flattenedMembers = Object.values(members).flatMap(({ members }) => members);

  // 내보내기 가능한 멤버 필터링
  const getKickableMembers = (memberList) => {
    return memberList.filter((m) => m.mberNo !== project.mberNo);
  };

  useEffect(() => {
    setMembers(groupMembersByOrg(participantList));
  }, [participantList]);

  const handleSearch = () => {
    const keyword = searched.trim().toLowerCase();
    if (!keyword) return;

    const filtered = participantList.filter((p) =>
      (p.mberNm || "").toLowerCase().includes(keyword),
    );

    setMembers(groupMembersByOrg(filtered));
    setIsSearch(true);
  };

  // 체크박스 상태 초기화
  const resetCheckboxState = () => {
    setChkMberList([]);
    setIsAllChecked(false);
  };

  // 멤버 개별 선택/해제
  const handleCheck = (mber) => {
    if (mber.mberNo === project.mberNo) return;

    const isAlreadyChecked = chkMberList.some((item) => item.mberNo === mber.mberNo);
    const updated = isAlreadyChecked
      ? chkMberList.filter((item) => item.mberNo !== mber.mberNo)
      : [...chkMberList, mber];

    setChkMberList(updated);

    const kickableMembers = getKickableMembers(flattenedMembers);
    setIsAllChecked(updated.length === kickableMembers.length);
  };

  // 멤버 전체 선택/해제
  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const kickableMembers = getKickableMembers(flattenedMembers);
      setChkMberList(kickableMembers);
      setIsAllChecked(true);
    } else {
      resetCheckboxState();
    }
  };

  const isChecked = (mberNo) => {
    return chkMberList.some((m) => m.mberNo === mberNo);
  };

  // 내보내기 확인 모달
  const handleKickModal = (members) => {
    const kickableMembers = getKickableMembers(members);

    if (kickableMembers.length === 0) {
      toast.error("내보내기 가능한 멤버가 존재하지 않습니다.");
      return;
    }

    const memberCount = kickableMembers.length;
    const message = `${memberCount === 1 ? "해당" : `${memberCount}명의`} 멤버를 내보내겠습니까?`;

    openModal({
      isOpen: true,
      type: "confirmation",
      title: "내보내기",
      message: [message],
      handleConfirm: () => handleKick(kickableMembers),
      handleCancel: () => closeModal(),
    });
  };

  // API: 멤버 내보내기
  const handleKick = async (members) => {
    try {
      const response = await axios.put("/api/kick", {
        mberNos: members.map((member) => member.mberNo),
        projectNo: project.projectNo,
        createMberId: user.mberNo,
      });

      if (response.status === 200) {
        const successMessage =
          members.length > 1
            ? "모든 멤버가 성공적으로 내보내기 되었습니다."
            : "해당 멤버가 성공적으로 내보내기 되었습니다.";
        toast.success(successMessage);
        await getParticipantList(project);
        resetCheckboxState();
      }
    } catch (error) {
      console.error("멤버 내보내기 실패:", error);
      toast.error("멤버 내보내기 중 오류가 발생했습니다.");
    } finally {
      closeModal();
    }
  };

  const columns = [
    { label: "", key: "checkbox" },
    { label: "이름", key: "mberNm" },
    { label: "전화번호", key: "moblphonNo" },
    { label: "내보내기", key: "kick" },
  ];

  const cellRenderers = {
    checkbox: (row) => (
      <input type="checkbox" checked={isChecked(row.mberNo)} onChange={() => handleCheck(row)} />
    ),
    mberNm: (row) => memberName(isGrp, row.mberNm),
    moblphonNo: (row) => formatPhoneNumber(row.moblphonNo),
    kick: (row) => {
      const canKick = row.mberNo !== project.mberNo;
      return (
        <button
          onClick={() => handleKickModal([row])}
          style={ButtonStateStyle(canKick)}
          disabled={!canKick}>
          내보내기
        </button>
      );
    },
  };

  return (
    <>
      {modal}
      <ModalBackground onClick={(e) => e.target === e.currentTarget && changeModalState("")}>
        <KickModalContainer>
          <div>
            <CloseButton changeModalState={changeModalState} />
            <SearchForm
              isGrp={isGrp}
              searched={searched}
              setSearched={setSearched}
              handleSearch={handleSearch}
            />
            <div className="section">
              {Object.entries(members).length > 0 ? (
                Object.entries(members).map(([orgNo, { orgNm, members: orgMembers }]) => (
                  <div key={orgNo}>
                    <h4>
                      <span>{orgNm}</span>
                      <span>{String(project.orgNo) !== orgNo && " (외부업체)"}</span>
                    </h4>
                    <MemberTable
                      isGrp={isGrp}
                      columns={columns}
                      rows={orgMembers}
                      isAllChecked={isAllChecked}
                      onCheckAll={handleCheckAll}
                      isChecked={isChecked}
                      onCheck={handleCheck}
                      renderCell={(row, key) => cellRenderers[key]?.(row) || null}
                    />
                  </div>
                ))
              ) : (
                <NotMessage>참여자가 없습니다.</NotMessage>
              )}
            </div>
            {flattenedMembers.length > 0 && (
              <button
                className="delete"
                onClick={() => handleKickModal(chkMberList)}
                disabled={chkMberList.length === 0}>
                내보내기
              </button>
            )}
          </div>
        </KickModalContainer>
      </ModalBackground>
    </>
  );
};

export default KickModal;
